import React, { useEffect, useMemo, useState } from 'react';
import { isDesktop, isMobile } from 'react-device-detect';
import { Loader } from 'uikit/loader/Loader';
import { Box } from '@primer/components';
import logoImage from 'components/sideNav/assets/logo.png';
import DownloadAppDesktopPreview from 'components/DownloadAppDesktopPreview/DownloadAppDesktopPreview';
import './EventUrlWithRedirectionSupport.scss';
import { getPlayStoreOrAppStoreUrl } from 'utils/common';
import {
    buildDownloadAppQrUrl,
    buildParticipantEventPath,
    getCanonicalDownloadAppRelativeUrl,
    parseDownloadAppEventParams,
    shouldSanitizeDownloadAppUrl,
} from 'utils/downloadAppQuery';

const readDownloadAppParamsFromLocation = () =>
    parseDownloadAppEventParams(
        window.location.search,
        window.location.href
    );

const EventUrlWithRedirectionSupport = () => {
    const [isShowDownloadAppSection, setIsShowDownloadAppSection] =
        useState(false);
    const [downloadAppParams, setDownloadAppParams] = useState(
        readDownloadAppParamsFromLocation
    );

    const {
        qrCodeUrl,
        continueInBrowserUrl,
        hasRedirectUrl,
        hasInvalidEventParams,
    } = useMemo(() => {
        const { origin } = window.location;
        const { eventId, pageId, hasEventContext, hasInvalidEventParams } =
            downloadAppParams;

        return {
            qrCodeUrl: buildDownloadAppQrUrl(origin, { eventId, pageId }),
            continueInBrowserUrl: hasEventContext
                ? `${origin}${buildParticipantEventPath(eventId, pageId)}`
                : `${origin}/`,
            hasRedirectUrl: hasEventContext,
            hasInvalidEventParams,
        };
    }, [downloadAppParams]);

    const redirectToStore = () => {
        const { playStoreOrAppStoreUrl } = getPlayStoreOrAppStoreUrl();
        window.location.href = playStoreOrAppStoreUrl;
    };

    useEffect(() => {
        const { pathname, search, href } = window.location;
        let parsed = readDownloadAppParamsFromLocation();

        if (shouldSanitizeDownloadAppUrl(pathname, search, href)) {
            const canonicalRelativeUrl = getCanonicalDownloadAppRelativeUrl(
                search,
                href
            );
            window.history.replaceState(
                {},
                document.title,
                canonicalRelativeUrl
            );
            const queryIndex = canonicalRelativeUrl.indexOf('?');
            const canonicalSearch =
                queryIndex >= 0
                    ? canonicalRelativeUrl.slice(queryIndex)
                    : '';
            parsed = parseDownloadAppEventParams(
                canonicalSearch,
                `${window.location.origin}${canonicalRelativeUrl}`
            );
            setDownloadAppParams(parsed);
        }

        if (isMobile && !parsed.hasInvalidEventParams) {
            redirectToStore();
            return;
        }
        setIsShowDownloadAppSection(true);
    }, []);

    const hanldeContinueOnWeb = () => {
        if (hasInvalidEventParams) {
            return;
        }
        window.location.href = continueInBrowserUrl;
    };

    const showContinue = !hasInvalidEventParams;

    return (
        <Box id="event-url-with-redirection">
            {!isShowDownloadAppSection ? (
                <Loader />
            ) : (
                <Box>
                    {isDesktop && (
                        <Box className="event-url-logo">
                            <img src={logoImage} alt="Covision" />
                        </Box>
                    )}
                    <Box className="download-app-page">
                        <Box display="flex" width="100%" flexWrap="wrap">
                            <Box
                                flex="1"
                                display="flex"
                                justifyContent="center"
                            >
                                {isDesktop || hasInvalidEventParams ? (
                                    <DownloadAppDesktopPreview
                                        qrCodeUrl={
                                            hasInvalidEventParams
                                                ? ''
                                                : qrCodeUrl
                                        }
                                        hasRedirectUrl={hasRedirectUrl}
                                        onContinue={
                                            showContinue
                                                ? hanldeContinueOnWeb
                                                : undefined
                                        }
                                    />
                                ) : (
                                    <Loader />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default EventUrlWithRedirectionSupport;
